import { useQuery } from '@tanstack/react-query';
import { queryKeyFactory } from 'utils/query-client';
import { loaders } from './surveys.loaders';
import { converters } from './surveys.converters';
import {
  SurveyPendingSubmissionsFilters,
  SurveysFilters,
  SurveySubmissionsFilters,
} from './surveys.types';

export const surveysKeys = queryKeyFactory('surveys');

export const useSurveys = (filters: Partial<SurveysFilters> = {}) => {
  return useQuery({
    queryKey: surveysKeys.list(filters),
    queryFn: () => loaders.getSurveys({ filters }),
    select: converters.getSurveys,
  });
};

export const useSurvey = (surveyId: string) => {
  return useQuery({
    queryKey: surveysKeys.detail(surveyId),
    queryFn: () => loaders.getSurvey({ surveyId }),
    select: converters.getSurvey,
    enabled: !!surveyId,
  });
};

export const useSurveyGroupAssignments = (surveyId: string) => {
  return useQuery({
    queryKey: surveysKeys.detail('group-assignments'),
    queryFn: () => loaders.getSurveyGroupAssignments({ surveyId }),
    select: converters.getSurveyGroupAssignments,
    enabled: !!surveyId,
  });
};

export const useSurveyResults = (surveyId: string) => {
  return useQuery({
    queryKey: surveysKeys.detail('results', surveyId),
    queryFn: () => loaders.getSurveyResults({ surveyId }),
    select: converters.getSurveyResults,
    enabled: !!surveyId,
  });
};

export const useSurveyPendingSubmissions = (
  surveyId: string,
  filters: Partial<SurveyPendingSubmissionsFilters> = {},
) => {
  return useQuery({
    queryKey: surveysKeys.detail('pending-submissions', surveyId, filters),
    queryFn: () => loaders.getSurveyPendingSubmissions({ surveyId, filters }),
    select: converters.getSurveyPendingSubmissions,
    enabled: !!surveyId,
  });
};

export const useSurveySubmissions = (
  surveyId: string,
  filters: Partial<SurveySubmissionsFilters> = {},
) => {
  return useQuery({
    queryKey: surveysKeys.detail('submissions', surveyId, filters),
    queryFn: () => loaders.getSurveySubmissions({ surveyId, filters }),
    select: converters.getSurveySubmissions,
    enabled: !!surveyId,
  });
};

export const useSurveySubmission = (surveyId: string, submissionId: string) => {
  return useQuery({
    queryKey: surveysKeys.detail('submissions', surveyId, submissionId),
    queryFn: () => loaders.getSurveySubmission({ surveyId, submissionId }),
    select: converters.getSurveySubmission,
    enabled: !!surveyId && !!submissionId,
  });
};
